import React from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import styled from "styled-components";
import striptags from "striptags";
import Layout from "../../../providers/Layout";
import {
  getLayoutProps,
  getArticleBySlug,
  getAgrarsklepProduct,
} from "../../../services/serverSideRequests";
import {
  fetchApi,
  getArticlesById,
  getArticleAuthors,
} from "../../../services/requests";
import { flattenResponsesArray } from "../../../services/arrays";
import {
  getFullCategoriesData,
  getUserIdFromCookies,
} from "../../../services/serverSideRequests";
import { Page, MainContent, Aside } from "../../../services/styles";
import { ArticleListBreadcrumbs } from "../../../components/Breadcrumbs/Breadcrumbs";
import ArticleComponent from "../../../components/Article/Article";
import ArticleOnListTeaser from "../../../components/ArticleOnListTeaser/ArticleOnListTeaser";
import BoxLegalAdvice from "../../../components/BoxLegalAdvice/BoxLegalAdvice";
import BoxBuySubscription from "../../../components/BoxBuySubscription/BoxBuySubscription";
import SpecialOfferBox from "../../../components/SpecialOfferBox/SpecialOfferBox";
import BoxNewsletter from "../../../components/BoxNewsletter/BoxNewsletter";
import BoxTags from "../../../components/BoxTags/BoxTags";
import BoxMostRead from "../../../components/BoxMostRead/BoxMostRead";
import ArticleMetaTags from "../../../components/MetaTags/ArticleMetaTags";
import MetaTags from "../../../components/MetaTags/MetaTags";
import { device } from "../../../services/styles";
import Ado from "../../../components/Ado/Ado";
import AdoCodes from "../../../components/Ado/AdoConfig.json";
import { v4 as uuidv4 } from "uuid";
import { redirectTo404 } from "../../../services/routing";
import { replaceArticlesPath } from "../../../services/urlString";

const Header = styled.p`
  font-size: 28px;
  line-height: 32px;
  font-weight: 900;
  margin: 40px 0 20px 0;
  text-align: center;

  @media ${device.tablet} {
    margin: 40px 0 20px 0;
    text-align: left;
  }
`;
const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Featured = styled.div`
  @media print {
    display: none;
  }
`;
const prepateDataForOfferBox = (data) => ({
  title: data.name,
  url: data.link,
  text: data.description,
  price: data.price,
  discountPrice: data.discount_price,
  image: data.fotourl,
  template: "offer",
});

const Article = ({
  navbarLinks,
  submenuLists,
  paths,
  mainCategories,
  importantArticles,
  article,
  category,
  mainCategory,
  featuredArticles,
  agrarsklepProduct,
  relatedArticles,
  authors,
  video,
}) => {
  const { asPath } = useRouter();
  const showAgrarsklepProduct = agrarsklepProduct?.active;
  return (
    <>
      <Head>
        <ArticleMetaTags article={article} />
      </Head>
      <Layout
        navbarLinks={navbarLinks}
        paths={paths}
        submenuLists={submenuLists}
        mainCategories={mainCategories}
        importantArticles={importantArticles}
        metatagsData={{
          description: striptags(article.lead),
          keywords: article.tags ? article.tags.join(", ") : "",
          href: `${process.env.site}${asPath}`,
          showAuthor: true,
          title: article.title,
        }}
      >
        <Page>
          <Aside>
            <BoxLegalAdvice />
            <BoxBuySubscription />
            <Ado {...AdoCodes.id04articleSidebar} />
            {showAgrarsklepProduct && (
              <SpecialOfferBox
                offer={prepateDataForOfferBox(agrarsklepProduct)}
                placeType={"sidebar"}
              />
            )}
            <Ado {...AdoCodes.id05articleSidebar} />
            <BoxMostRead articles={relatedArticles} />
            <BoxNewsletter />
            <BoxTags tags={article.tags} />
          </Aside>
          <MainContent>
            <ArticleListBreadcrumbs
              categorySlug={article.category.slug}
              categoryName={article.category.name}
              mainCategory={mainCategory}
              article={article.title}
            />
            <ArticleComponent
              article={article}
              authors={authors}
              video={video}
            />
            <Ado {...AdoCodes.id02articleUnderArticle} />
            <Ado {...AdoCodes.id03articleUnderComments} />
            <Featured>
              <Header>Przeczytaj także</Header>
              <Container>
                {featuredArticles.map((article) => {
                  return (
                    <ArticleOnListTeaser key={article.id} article={article} />
                  );
                })}
              </Container>
            </Featured>
          </MainContent>
        </Page>
      </Layout>
    </>
  );
};

export async function getServerSideProps(context) {
  function flattenPayloads(payloads) {
    return payloads.map((p) => p[0]).filter(Boolean);
  }

  const userId = await getUserIdFromCookies(context);
  const layoutProps = await getLayoutProps();
  const [article, error, { status }] = await getArticleBySlug(
    context.params.article,
    userId
  );
  if (error && status === 404) {
    redirectTo404(context);
    return;
  }

  const authorsPromise = getArticleAuthors(article?.journalists || []);
  const { category, mainCategory } = await getFullCategoriesData(
    context.params.category
  );
  const featuredArticlesPromise = getArticlesById(
    article.featured_articles.slice(0, 3)
  );
  const relatedArticlesPromise = getArticlesById(
    article.related_articles.slice(0, 3)
  );
  const authors = flattenPayloads(await authorsPromise);
  const featuredArticles = flattenPayloads(await featuredArticlesPromise);
  const relatedArticles = flattenPayloads(await relatedArticlesPromise);

  const [agrarsklepProduct] = await getAgrarsklepProduct(article.id);

  const [video] = article.video
    ? await fetchApi({ url: `videos-video/${article.video}` })
    : [null];

  return {
    props: {
      article,
      category,
      mainCategory,
      featuredArticles,
      relatedArticles,
      agrarsklepProduct,
      authors,
      video,
      ...layoutProps,
    },
  };
}
export default Article;
